import lodash from 'lodash'
import store from '@/store'

import { buildEmail, getBuildingUsersEmails, getLinkToTab } from './util'
import { directoryRouteNames } from '@/utils/consts'
import { DIR_STATUS, DIR_ACTION, DIR_TYPE } from '@/modules/directories/consts'
import { getBuildingInfoById } from '@/api/buildings/request'
import { PermTypeMapKeys } from '@/utils/aclHelper'
import { SEND_EMAIL } from './consts'
import dateUtil from '@/utils/dateUtil'
import i18n from '@/utils/i18n'
import userHelper from '@/utils/userHelper'
import { displayI18nFullName } from '@/utils/fullName'

function getObjectValue (object, propertyString) {
  const ret = lodash.get(object, propertyString, i18n.t('emptyValue')) || i18n.t('emptyValue')
  return Array.isArray(ret) ? ret.join(',') : ret
}

function findHistoryByStatus (histories, status) {
  return lodash.find(histories, {status})
}

function getDirectoryBrief (directory) {
  const type = directory.type || ''
  let floor = (directory.type === DIR_TYPE.FLOOR
    ? lodash.get(directory, 'floor')
    : lodash.get(directory, 'companies[0].floor')) || ''

  // Revert floor value to '' if it matches empty value
  floor = floor === i18n.t('emptyValue') ? '' : floor

  return {
    isAdding: directory.action === DIR_ACTION.ADD,
    isRemoval: directory.action === DIR_ACTION.REMOVE,
    displayName: lodash.get(directory, 'companies[0].engName', ''),
    floor,
    premises: lodash.get(directory, 'companies[0].premises', ''),
    effectiveDate: directory.effectiveDate ? dateUtil.format(directory.effectiveDate, dateUtil.DD_MMM_YYYY) : i18n.t('emptyValue'),
    removeDate: directory.removeDate ? dateUtil.format(directory.removeDate, dateUtil.DD_MMM_YYYY) : i18n.t('emptyValue'),
    comment: getObjectValue(directory, 'comment'),
    type: lodash.capitalize(type),
  }
}

function getActionText (directories) {
  const hasAdding = directories.some(item => item.action === DIR_ACTION.ADD)
  const hasRemoval = directories.some(item => item.action === DIR_ACTION.REMOVE)

  if (hasAdding && hasRemoval) return i18n.t('directory.action.addingAndRemoval')
  if (hasAdding) return i18n.t('directory.action.adding')
  if (hasRemoval) return i18n.t('directory.action.removal')

  return ''
}

async function buildMetaData (directories, permTypeKey, isToTenant = false) {
  let metaData = {
    tenancyInfo: {},
    subsidiary: i18n.t('system.title'),
  }
  let buildingUsersEmails
  let tenancy
  let building
  const routeName = isToTenant ? directoryRouteNames.tenantDirectories : directoryRouteNames.tenancyDirectories
  try {
    const itemWithTenancy = directories.find(item => item.tenancy)
    tenancy = lodash.cloneDeep(itemWithTenancy.tenancy)
    const companyName = deep(tenancy, 'company.name') || i18n.t('emptyValue')
    metaData.tenancyInfo = {
      ...tenancy,
      buildingCode: lodash.get(tenancy, 'building.code'),
    }
    metaData.tenancyInfo.companyName = companyName

    if (permTypeKey) {
      buildingUsersEmails = await getBuildingUsersEmails(tenancy.buildingId, tenancy.type, permTypeKey)
    }
    building = await getBuildingInfoById(tenancy.buildingId)
    metaData.tenancyInfo.building = building.fullName

    metaData.link = getLinkToTab(routeName, { id: tenancy.id }, {}, process.env.ENGINEER_HOST_NAME)
    metaData.linkToMaintain = getLinkToTab(directoryRouteNames.directoryOrder, { id: tenancy.id }, {}, process.env.ENGINEER_HOST_NAME)
    metaData.key = tenancy.id
  } catch (error) {
    console.error('directory.tenancy is empty', error)
  }
  metaData.directories = directories.map(item => ({
    tenancyInfo: metaData.tenancyInfo,
    ...getDirectoryBrief(item),
  }))
  metaData.actionText = getActionText(directories)
  return [lodash.uniq(buildingUsersEmails), metaData, tenancy, building]
}

async function waitForApproval (directories, type) {
  let emails = []
  let [buildingUsersEmails, metaData] = await buildMetaData(directories, PermTypeMapKeys.APPROVE_DIRECTORY)
  buildEmail(emails, buildingUsersEmails, type, metaData, SEND_EMAIL)
  return emails
}

async function waitForProcessing (directories, type) {
  let emails = []
  let [buildingUsersEmails, metaData] = await buildMetaData(directories, PermTypeMapKeys.MAINTAIN_DIRECTORY)
  buildEmail(emails, buildingUsersEmails, type, metaData, SEND_EMAIL)
  return emails
}

export function getDirectorySubmitter (directory) {
  const sortedHistories = lodash.orderBy(
    directory.histories,
    [item => new Date(item.createdAt)], ['desc'],
  )
  const submittedHistory = findHistoryByStatus(sortedHistories, DIR_STATUS.SUBMITTED)
  return submittedHistory.who
}

async function submitted (directories, type) {
  let emails = []
  const isTenant = store.getters['user/isTenant']
  let [buildingUsersEmails, metaData] = await buildMetaData(
    directories,
    PermTypeMapKeys.DIRECTORY_SUBMITTED_BCC_GROUP,
    isTenant,
  )
  const currentUser = store.getters['user/currentUser']
  const currentUserEmail = store.getters['user/currentUserEmail']
  let displayName = currentUser.name
  const tenancyAccounts = lodash.get(metaData, 'tenancyInfo.accounts')
  if (isTenant && tenancyAccounts) {
    const account = tenancyAccounts.find(account => account.id === currentUser.id)
    if (account) { displayName = displayI18nFullName(account.TenancyAccount.contactFirstName, account.TenancyAccount.contactLastName) }
  }
  metaData.bcc = buildingUsersEmails
  metaData.userName = displayName

  buildEmail(emails, currentUserEmail, type, metaData, SEND_EMAIL)
  return emails
}

async function notifyTenant ({
  directories,
  type,
  bccPermKey,
}) {
  let emails = []
  const submitter = getDirectorySubmitter(directories[0])
  let [buildingUsersEmails, metaData] = await buildMetaData(directories, bccPermKey, submitter.isTenant)
  metaData.userName = userHelper.getUserName(submitter.username, submitter.displayName)
  metaData.bcc = buildingUsersEmails
  buildEmail(emails, [submitter.email], type, metaData, SEND_EMAIL)
  return emails
}

/**
 * The approved email is not in use anymore, we keep it in case client changes mind
 * Remove Approval Notification to Tenant https://jirapct.epam.com/jira/browse/HKLDCMS-26377
 */
async function approved (directories, type) {
  return notifyTenant({
    directories,
    type,
    bccPermKey: PermTypeMapKeys.DIRECTORY_APPROVED_BCC_GROUP,
  })
}

async function rejected (directories, type) {
  return notifyTenant({
    directories,
    type,
    bccPermKey: PermTypeMapKeys.DIRECTORY_REJECTED_BCC_GROUP,
  })
}

const directoryNotification = {
  waitForApproval: waitForApproval,
  waitForProcessing: waitForProcessing,
  approved: approved,
  rejected: rejected,
  submitted: submitted,
}

export default directoryNotification
